import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import type { AdminCenterDataRequest } from '@routes/sidebar/___shared___/api/adminCenterData.api.ts';
import { ADMIN_CENTER_DATA } from '@routes/sidebar/___shared___/api/constants.ts';
import type {
    CountriesTypesColumns,
    EntityLocationTypesColumns,
    FlagsTypesColumns,
    FuelTypeCategoriesTypesColumns,
    FuelTypesTypesColumns,
    ShoreDepartmentsTypesColumns,
    ShoreDeptTypeTypesColumns,
    ShoreTeamTypesColumns,
    ShoreUserTitleTypesColumns,
    TradingAreasTypesColumns,
    VesselEquipmentDetailsTypesColumns,
    VesselEquipmentMakersTypesColumns,
    VesselGroupsTypesColumns,
    VesselTypesColumns,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/types.ts';
import { translateLazy } from '@utils/translations.ts';

export const REFERENCES_STEP_TITLE = 'references';
export const RANK_SET_NAME_FIELD = 'RankSetName';
export const NEW_RANK_SET_NAME_FIELD = 'NewRankSetName';

export const REFERENCES_TABLE_NAMES = {
    RANKS: 'ranks',
    COUNTRIES: 'countries',
    FLAGS: 'flags',
    VESSEL_EQUIPMENT_MAKER: 'vesselEquipmentMaker',
    VESSEL_EQUIPMENT_DETAILS: 'vesselEquipmentDetails',
    TRADING_AREAS: 'tradingAreas',
    ENTITY_LOCATION: 'entityLocation',
    SHORE_USER_TITLE: 'shoreUserTitle',
    SHORE_DEPT_TYPE: 'shoreDeptType',
    SHORE_DEPARTMENTS: 'shoreDepartments',
    SHORE_TEAM: 'shoreTeam',
    FUEL_TYPE_CATEGORIES: 'fuelTypeCategories',
    FUEL_TYPES: 'fuelTypes',
    VESSEL_GROUPS: 'vesselGroups',
    VESSEL_TYPES: 'vesselTypes',
};

export const referencesTableOptions = [
    { value: REFERENCES_TABLE_NAMES.RANKS, label: translateLazy(msg`Ranks`) },
    { value: REFERENCES_TABLE_NAMES.COUNTRIES, label: translateLazy(msg`Countries`) },
    { value: REFERENCES_TABLE_NAMES.FLAGS, label: translateLazy(msg`Flags`) },
    { value: REFERENCES_TABLE_NAMES.VESSEL_EQUIPMENT_MAKER, label: translateLazy(msg`Vessel Equipment Makers`) },
    { value: REFERENCES_TABLE_NAMES.VESSEL_EQUIPMENT_DETAILS, label: translateLazy(msg`Vessel Equipment Details`) },
    { value: REFERENCES_TABLE_NAMES.TRADING_AREAS, label: translateLazy(msg`Trading Areas`) },
    { value: REFERENCES_TABLE_NAMES.ENTITY_LOCATION, label: translateLazy(msg`Entity Locations`) },
    { value: REFERENCES_TABLE_NAMES.SHORE_USER_TITLE, label: translateLazy(msg`Shore User Titles`) },
    { value: REFERENCES_TABLE_NAMES.SHORE_DEPT_TYPE, label: translateLazy(msg`Shore Department Types`) },
    { value: REFERENCES_TABLE_NAMES.SHORE_DEPARTMENTS, label: translateLazy(msg`Shore Departments`) },
    { value: REFERENCES_TABLE_NAMES.SHORE_TEAM, label: translateLazy(msg`Shore Teams`) },
    { value: REFERENCES_TABLE_NAMES.FUEL_TYPE_CATEGORIES, label: translateLazy(msg`Fuel Type Categories`) },
    { value: REFERENCES_TABLE_NAMES.FUEL_TYPES, label: translateLazy(msg`Fuel Types`) },
    { value: REFERENCES_TABLE_NAMES.VESSEL_GROUPS, label: translateLazy(msg`Vessel Groups`) },
    { value: REFERENCES_TABLE_NAMES.VESSEL_TYPES, label: translateLazy(msg`Vessel Types`) },
];

export const COMMON_VALIDATION_ERROR_MSG = translateLazy(
    msg`The row contains validation errors. In order to save, please correct the highlighted fields`,
);
export const COMMON_VALIDATION_ERROR_LABEL = translateLazy(msg`Validation error`);

//ranks
export const RANKS_COLUMN_DATA_INDEX = {
    BB_RANK_AND_CODE: 'bbRankAndCode',
    CLIENT_RANKS: 'clientRank',
    CLIENT_CODE: 'clientCode',
    VESSEL_DEPARTMENT: 'vesselDepartmentId',
    PROFICIENCY_LEVEL: 'proficiencyLevelId',
    PROMOTION_RANK: 'promotionRankId',
    SUPERVISING_RANK: 'supervisingRankId',
    IS_ACTIVE: 'isActive',
} as const;

export const RANKS_COLUMN_TITLE = {
    BB_RANK_AND_CODE: msg`BB Rank and Code`,
    CLIENT_RANKS: msg`Client Rank*`,
    CLIENT_CODE: msg`Client Code*`,
    VESSEL_DEPARTMENT: msg`Department*`,
    PROFICIENCY_LEVEL: msg`Proficiency Level*`,
    PROMOTION_RANK: msg`Promotion rank`,
    SUPERVISING_RANK: msg`Supervising rank`,
    IS_ACTIVE: msg`Status`,
} as const;

export const RANKS_ENTITY_TYPE = 'RankReference';

export const RANKS_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    TENANT_SHORT_CODE: 'TenantShortCode',
    TENANT_NAME: 'TenantName',
    VESSEL_DEPARTMENT_ID: 'VesselDepartmentId',
    PROFICIENCY_LEVEL_ID: 'ProficiencyLevelId',

    // only backend validation fields
} as const;

export const RANKS_COLUMN_SORT_MAP: Record<string, string> = {
    [RANKS_COLUMN_DATA_INDEX.CLIENT_RANKS]: 'tenantName',
    [RANKS_COLUMN_DATA_INDEX.CLIENT_CODE]: 'tenantShortCode',
    [RANKS_COLUMN_DATA_INDEX.VESSEL_DEPARTMENT]: 'vesselDepartmentName',
    [RANKS_COLUMN_DATA_INDEX.PROFICIENCY_LEVEL]: 'proficiencyLevelName',
    [RANKS_COLUMN_DATA_INDEX.PROMOTION_RANK]: 'promotionRankName',
    [RANKS_COLUMN_DATA_INDEX.SUPERVISING_RANK]: 'supervisingRankName',
};

export const RANKS_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [RANKS_COLUMN_DATA_INDEX.BB_RANK_AND_CODE]: RANKS_COLUMN_TITLE.BB_RANK_AND_CODE,
    [RANKS_COLUMN_DATA_INDEX.CLIENT_RANKS]: RANKS_COLUMN_TITLE.CLIENT_RANKS,
    [RANKS_COLUMN_DATA_INDEX.CLIENT_CODE]: RANKS_COLUMN_TITLE.CLIENT_CODE,
    [RANKS_COLUMN_DATA_INDEX.VESSEL_DEPARTMENT]: RANKS_COLUMN_TITLE.VESSEL_DEPARTMENT,
    [RANKS_COLUMN_DATA_INDEX.PROFICIENCY_LEVEL]: RANKS_COLUMN_TITLE.PROFICIENCY_LEVEL,
    [RANKS_COLUMN_DATA_INDEX.PROMOTION_RANK]: RANKS_COLUMN_TITLE.PROMOTION_RANK,
    [RANKS_COLUMN_DATA_INDEX.SUPERVISING_RANK]: RANKS_COLUMN_TITLE.SUPERVISING_RANK,
    [RANKS_COLUMN_DATA_INDEX.IS_ACTIVE]: RANKS_COLUMN_TITLE.IS_ACTIVE,
};

export const RANKS_DATASOURCE_REFS = {
    VESSEL_DEPARTMENTS: 'RefVesselDepartments',
    PROFICIENCY_LEVELS: 'RefProficiencyLevels',
    CLIENT_RANKS: 'RefClientRanks',
    CLIENT_CODES: 'RefClientCodes',
    PROMOTION_SUPERVISOR_RANKS: 'RefPromotionSupervisorRanks',
    BB_RANK_AND_CODE: 'RefBBRankAndCode',
};

export const RANKS_DATA_PAYLOAD: AdminCenterDataRequest = {
    dataToReturn: [
        ADMIN_CENTER_DATA.VESSEL_DEPARTMENTS,
        ADMIN_CENTER_DATA.PROFICIENCY_LEVELS,
        ADMIN_CENTER_DATA.RANK_SHORT_CODES,
    ],
};

// vesselTypes-table
export const VESSEL_TYPES_COLUMN_DATA_INDEX = {
    VESSEL_TYPE: 'vesselType',
    TYPE_CODE: 'typeCode',
    VESSEL_SUBTYPE: 'vesselSubtype',
    SUBTYPE_CODE: 'subTypeCode',
};

export const VESSEL_TYPES_COLUMN_TITLE = {
    VESSEL_TYPE: msg`Vessel Type`,
    TYPE_CODE: msg`Type Code`,
    VESSEL_SUBTYPE: msg`Vessel Subtype`,
    SUBTYPE_CODE: msg`Subtype Code`,
} as const;

export const VESSEL_TYPES_COLUMN_FILTER_MAP: Record<string, string> = {
    [VESSEL_TYPES_COLUMN_DATA_INDEX.VESSEL_TYPE]: VESSEL_TYPES_COLUMN_DATA_INDEX.VESSEL_TYPE,
    [VESSEL_TYPES_COLUMN_DATA_INDEX.TYPE_CODE]: VESSEL_TYPES_COLUMN_DATA_INDEX.TYPE_CODE,
    [VESSEL_TYPES_COLUMN_DATA_INDEX.VESSEL_SUBTYPE]: VESSEL_TYPES_COLUMN_DATA_INDEX.VESSEL_SUBTYPE,
    [VESSEL_TYPES_COLUMN_DATA_INDEX.SUBTYPE_CODE]: VESSEL_TYPES_COLUMN_DATA_INDEX.SUBTYPE_CODE,
};

export const VESSEL_TYPES_COLUMN_SORT_MAP: Record<VesselTypesColumns, string> = {
    [VESSEL_TYPES_COLUMN_DATA_INDEX.VESSEL_TYPE]: 'vesselTypeType',
    [VESSEL_TYPES_COLUMN_DATA_INDEX.TYPE_CODE]: 'vesselTypeShortCode',
    [VESSEL_TYPES_COLUMN_DATA_INDEX.VESSEL_SUBTYPE]: 'type',
    [VESSEL_TYPES_COLUMN_DATA_INDEX.SUBTYPE_CODE]: 'shortCode',
};

// countries-table
export const COUNTRIES_COLUMN_DATA_INDEX = {
    COUNTRY_NAME: 'countryName',
    ISO_ALPHA_2: 'ISOAlpha2',
    ISO_ALPHA_3: 'ISOAlpha3',
    COUNTRY_CODE: 'countryCode',
    NATIONALITY: 'nationality',
};

export const COUNTRIES_COLUMN_TITLE = {
    COUNTRY_NAME: msg`Country Name`,
    ISO_ALPHA_2: msg`ISO Alpha 2`,
    ISO_ALPHA_3: msg`ISO Alpha 3`,
    COUNTRY_CODE: msg`Country Code`,
    NATIONALITY: msg`Nationality`,
} as const;

export const COUNTRIES_COLUMN_SORT_MAP: Record<CountriesTypesColumns, string> = {
    [COUNTRIES_COLUMN_DATA_INDEX.COUNTRY_NAME]: 'country',
    [COUNTRIES_COLUMN_DATA_INDEX.ISO_ALPHA_2]: 'isoAlpha2',
    [COUNTRIES_COLUMN_DATA_INDEX.ISO_ALPHA_3]: 'isoAlpha3',
    [COUNTRIES_COLUMN_DATA_INDEX.COUNTRY_CODE]: COUNTRIES_COLUMN_DATA_INDEX.COUNTRY_CODE,
    [COUNTRIES_COLUMN_DATA_INDEX.NATIONALITY]: COUNTRIES_COLUMN_DATA_INDEX.NATIONALITY,
};

// flags-table
export const FLAGS_COLUMN_DATA_INDEX = {
    FLAG_CODE: 'flagCode',
    FLAG_JURISDICTION: 'flagJurisdiction',
};

export const FLAGS_COLUMN_TITLE = {
    FLAG_CODE: msg`Flag Code`,
    FLAG_JURISDICTION: msg`Flag State`,
} as const;

export const FLAGS_COLUMN_SORT_MAP: Record<FlagsTypesColumns, string> = {
    [FLAGS_COLUMN_DATA_INDEX.FLAG_CODE]: FLAGS_COLUMN_DATA_INDEX.FLAG_CODE,
    [FLAGS_COLUMN_DATA_INDEX.FLAG_JURISDICTION]: FLAGS_COLUMN_DATA_INDEX.FLAG_JURISDICTION,
};

// vesselEquipmentMakers-table
export const VESSEL_EQUIPMENT_MAKERS_COLUMN_DATA_INDEX = {
    NAME_SHORT: 'nameShort',
    NAME_FULL: 'nameFull',
    IS_BUILT_IN: 'isBuiltIn',
};

export const VESSEL_EQUIPMENT_MAKERS_COLUMN_TITLE = {
    NAME_SHORT: msg`Short Name*`,
    NAME_FULL: msg`Full Name*`,
    IS_BUILT_IN: msg`Built-in`,
} as const;

export const VESSEL_EQUIPMENT_MAKER_ENTITY_TYPE = 'VesselEquipmentMaker';

export const VESSEL_EQUIPMENT_MAKERS_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    NAME_SHORT: 'NameShort',
    NAME_FULL: 'NameFull',
} as const;

export const VESSEL_EQUIPMENT_MAKERS_COLUMN_SORT_MAP: Record<VesselEquipmentMakersTypesColumns, string> = {
    [VESSEL_EQUIPMENT_MAKERS_COLUMN_DATA_INDEX.NAME_SHORT]: VESSEL_EQUIPMENT_MAKERS_COLUMN_DATA_INDEX.NAME_SHORT,
    [VESSEL_EQUIPMENT_MAKERS_COLUMN_DATA_INDEX.NAME_FULL]: VESSEL_EQUIPMENT_MAKERS_COLUMN_DATA_INDEX.NAME_FULL,
};

export const VESSEL_EQUIPMENT_MAKERS_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [VESSEL_EQUIPMENT_MAKERS_COLUMN_DATA_INDEX.NAME_SHORT]: VESSEL_EQUIPMENT_MAKERS_COLUMN_TITLE.NAME_SHORT,
    [VESSEL_EQUIPMENT_MAKERS_COLUMN_DATA_INDEX.NAME_FULL]: VESSEL_EQUIPMENT_MAKERS_COLUMN_TITLE.NAME_FULL,
};

//"VesselEquipmentType.Name|VesselEquipmentType.ShortCode|VesselEquipmentMaker.NameShort|VesselEquipmentMaker.NameFull|FilterText|Model|CreatedAtUTC",

// vesselEquipmentDetails-table
export const VESSEL_EQUIPMENT_DETAILS_COLUMN_DATA_INDEX = {
    SHORT_CODE: 'VesselEquipmentTypeId',
    NAME_SHORT: 'vesselEquipmentMakerId',
    MODEL: 'model',
    FILTER_TEXT: 'filterText',
    IS_BUILT_IN: 'isBuiltIn',
};

export const VESSEL_EQUIPMENT_DETAILS_COLUMN_TITLE = {
    SHORT_CODE: msg`Type code*`,
    NAME_SHORT: msg`Maker name*`,
    MODEL: msg`Model*`,
    FILTER_TEXT: msg`Filter text*`,
    IS_BUILT_IN: msg`Built-in`,
} as const;

export const VESSEL_EQUIPMENT_DETAILS_ENTITY_TYPE = 'VesselEquipmentDetail';

export const VESSEL_EQUIPMENT_DETAILS_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    VESSEL_EQUIPMENT_TYPE_ID: 'VesselEquipmentTypeId',
    VESSEL_EQUIPMENT_MAKER_ID: 'VesselEquipmentMakerId',
    MODEL: 'Model',
    FILTER_TEXT: 'FilterText',
} as const;

export const VESSEL_EQUIPMENT_DETAILS_COLUMN_SORT_MAP: Record<VesselEquipmentDetailsTypesColumns, string> = {
    [VESSEL_EQUIPMENT_DETAILS_COLUMN_DATA_INDEX.SHORT_CODE]: 'vesselEquipmentTypeShortCode',
    [VESSEL_EQUIPMENT_DETAILS_COLUMN_DATA_INDEX.NAME_SHORT]: 'vesselEquipmentMakerNameShort',
    [VESSEL_EQUIPMENT_DETAILS_COLUMN_DATA_INDEX.MODEL]: 'model',
    [VESSEL_EQUIPMENT_DETAILS_COLUMN_DATA_INDEX.FILTER_TEXT]: 'filterText',
};

export const VESSEL_EQUIPMENT_DETAILS_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [VESSEL_EQUIPMENT_DETAILS_COLUMN_DATA_INDEX.SHORT_CODE]: VESSEL_EQUIPMENT_DETAILS_COLUMN_TITLE.SHORT_CODE,
    [VESSEL_EQUIPMENT_DETAILS_COLUMN_DATA_INDEX.NAME_SHORT]: VESSEL_EQUIPMENT_DETAILS_COLUMN_TITLE.NAME_SHORT,
    [VESSEL_EQUIPMENT_DETAILS_COLUMN_DATA_INDEX.MODEL]: VESSEL_EQUIPMENT_DETAILS_COLUMN_TITLE.MODEL,
    [VESSEL_EQUIPMENT_DETAILS_COLUMN_DATA_INDEX.FILTER_TEXT]: VESSEL_EQUIPMENT_DETAILS_COLUMN_TITLE.FILTER_TEXT,
};

export const VESSEL_EQUIPMENT_DETAILS_DATASOURCE_REFS = {
    TYPES: 'RefVesselEquipmentTypes',
    MAKERS: 'RefVesselEquipmentMakers',
};

// tradingAreas-table
export const TRADING_AREAS_COLUMN_DATA_INDEX = {
    TRADING_AREA_NAME: 'tradingAreaName',
    RESTRICTION_NOTES: 'restrictionNotes',
    IS_BUILT_IN: 'isBuiltIn',
};

export const TRADING_AREAS_COLUMN_TITLE = {
    TRADING_AREA_NAME: msg`Trading Area name*`,
    RESTRICTION_NOTES: msg`Trading area restriction notes*`,
    IS_BUILT_IN: msg`Built-in`,
} as const;

export const TRADING_AREAS_ENTITY_TYPE = 'TradingArea';

export const TRADING_AREAS_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    TRADING_AREA_NAME: 'TradingAreaName',
    RESTRICTION_NOTES: 'RestrictionNotes',
} as const;

export const TRADING_AREAS_COLUMN_SORT_MAP: Record<TradingAreasTypesColumns, string> = {
    [TRADING_AREAS_COLUMN_DATA_INDEX.TRADING_AREA_NAME]: TRADING_AREAS_COLUMN_DATA_INDEX.TRADING_AREA_NAME,
    [TRADING_AREAS_COLUMN_DATA_INDEX.RESTRICTION_NOTES]: TRADING_AREAS_COLUMN_DATA_INDEX.RESTRICTION_NOTES,
};

export const TRADING_AREAS_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [TRADING_AREAS_COLUMN_DATA_INDEX.TRADING_AREA_NAME]: TRADING_AREAS_COLUMN_TITLE.TRADING_AREA_NAME,
    [TRADING_AREAS_COLUMN_DATA_INDEX.RESTRICTION_NOTES]: TRADING_AREAS_COLUMN_TITLE.RESTRICTION_NOTES,
};

// entityLocation-table
export const ENTITY_LOCATION_COLUMN_DATA_INDEX = {
    ENTITY_NAME: 'name',
    ADDRESS_ONE: 'address1',
    ADDRESS_TWO: 'address2',
    CITY: 'city',
    STATE_COUNTY_PROVINCE: 'stateCountyProvince',
    POSTAL_CODE: 'zipCode',
    COUNTRY: 'countryId',
    COUNTRY_NAME_FILTER: 'countryName',
    IS_BUILT_IN: 'isBuiltIn',
};

export const ENTITY_LOCATION_COLUMN_TITLE = {
    ENTITY_NAME: msg`Entity Name*`,
    ADDRESS_ONE: msg`Address 1`,
    ADDRESS_TWO: msg`Address 2`,
    CITY: msg`City`,
    STATE_COUNTY_PROVINCE: msg`State/County/Province`,
    POSTAL_CODE: msg`Postal/ZipCode`,
    COUNTRY: msg`Country`,
    IS_BUILT_IN: msg`Built-in`,
} as const;

export const ENTITY_LOCATION_ENTITY_TYPE = 'ClientTenantEntityLocation';

export const ENTITY_LOCATION_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    NAME: 'Name',
    ADDRESS1: 'Address1',
    ADDRESS2: 'Address2',
    CITY: 'City',
    STATE_COUNTY_PROVINCE: 'StateCountyProvince',
    ZIP_CODE: 'ZipCode',

    // only backend validation fields
    COUNTRY: 'CountryId',
} as const;

export const ENTITY_LOCATION_COLUMN_SORT_MAP: Record<EntityLocationTypesColumns, string> = {
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.ENTITY_NAME]: ENTITY_LOCATION_COLUMN_DATA_INDEX.ENTITY_NAME,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.ADDRESS_ONE]: ENTITY_LOCATION_COLUMN_DATA_INDEX.ADDRESS_ONE,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.ADDRESS_TWO]: ENTITY_LOCATION_COLUMN_DATA_INDEX.ADDRESS_TWO,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.CITY]: ENTITY_LOCATION_COLUMN_DATA_INDEX.CITY,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.STATE_COUNTY_PROVINCE]: ENTITY_LOCATION_COLUMN_DATA_INDEX.STATE_COUNTY_PROVINCE,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.POSTAL_CODE]: ENTITY_LOCATION_COLUMN_DATA_INDEX.POSTAL_CODE,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.COUNTRY]: 'countryName',
};

export const ENTITY_LOCATION_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.ENTITY_NAME]: ENTITY_LOCATION_COLUMN_TITLE.ENTITY_NAME,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.ADDRESS_ONE]: ENTITY_LOCATION_COLUMN_TITLE.ADDRESS_ONE,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.ADDRESS_TWO]: ENTITY_LOCATION_COLUMN_TITLE.ADDRESS_TWO,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.CITY]: ENTITY_LOCATION_COLUMN_TITLE.CITY,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.STATE_COUNTY_PROVINCE]: ENTITY_LOCATION_COLUMN_TITLE.STATE_COUNTY_PROVINCE,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.POSTAL_CODE]: ENTITY_LOCATION_COLUMN_TITLE.POSTAL_CODE,
    [ENTITY_LOCATION_COLUMN_DATA_INDEX.COUNTRY]: ENTITY_LOCATION_COLUMN_TITLE.COUNTRY,
};

export const ENTITY_LOCATION_DATASOURCE_REFS = {
    COUNTRIES: 'RefCountries',
};

// shoreUserTitle-table
export const SHORE_USER_TITLE_COLUMN_DATA_INDEX = {
    TITLE: 'title',
    CODE: 'code',
    IS_BUILT_IN: 'isBuiltIn',
};

export const SHORE_USER_TITLE_COLUMN_TITLE = {
    TITLE: msg`Shore User Title*`,
    CODE: msg`Shore User Title Code*`,
    IS_BUILT_IN: msg`Built-in`,
} as const;

export const SHORE_USER_TITLE_ENTITY_TYPE = 'ShoreUserTitle';

export const SHORE_USER_TITLE_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    TITLE: 'Title',
    CODE: 'Code',
} as const;

export const SHORE_USER_TITLE_COLUMN_SORT_MAP: Record<ShoreUserTitleTypesColumns, string> = {
    [SHORE_USER_TITLE_COLUMN_DATA_INDEX.TITLE]: SHORE_USER_TITLE_COLUMN_DATA_INDEX.TITLE,
    [SHORE_USER_TITLE_COLUMN_DATA_INDEX.CODE]: SHORE_USER_TITLE_COLUMN_DATA_INDEX.CODE,
};

export const SHORE_USER_TITLE_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [SHORE_USER_TITLE_COLUMN_DATA_INDEX.TITLE]: SHORE_USER_TITLE_COLUMN_TITLE.TITLE,
    [SHORE_USER_TITLE_COLUMN_DATA_INDEX.CODE]: SHORE_USER_TITLE_COLUMN_TITLE.CODE,
};

// shoreDeptType-table
export const SHORE_DEPT_TYPE_COLUMN_DATA_INDEX = {
    TYPE: 'type',
    CODE: 'code',
    IS_LIST_DEPARTMENT: 'isListDepartment',
    IS_BUILT_IN: 'isBuiltIn',
};

export const SHORE_DEPT_TYPE_COLUMN_TITLE = {
    TYPE: msg`Shore Dept Type*`,
    CODE: msg`Shore Dept Type Code*`,
    IS_LIST_DEPARTMENT: msg`List under department`,
    IS_BUILT_IN: msg`Built-in`,
} as const;

export const SHORE_DEPT_TYPE_ENTITY_TYPE = 'ShoreDepartmentType';

export const SHORE_DEPT_TYPE_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    TYPE: 'Type',
    CODE: 'Code',
} as const;

export const SHORE_DEPT_TYPE_COLUMN_SORT_MAP: Record<ShoreDeptTypeTypesColumns, string> = {
    [SHORE_DEPT_TYPE_COLUMN_DATA_INDEX.TYPE]: SHORE_DEPT_TYPE_COLUMN_DATA_INDEX.TYPE,
    [SHORE_DEPT_TYPE_COLUMN_DATA_INDEX.CODE]: SHORE_DEPT_TYPE_COLUMN_DATA_INDEX.CODE,
};

export const SHORE_DEPT_TYPE_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [SHORE_DEPT_TYPE_COLUMN_DATA_INDEX.TYPE]: SHORE_DEPT_TYPE_COLUMN_TITLE.TYPE,
    [SHORE_DEPT_TYPE_COLUMN_DATA_INDEX.CODE]: SHORE_DEPT_TYPE_COLUMN_TITLE.CODE,
};

// shoreDepartments-table
export const SHORE_DEPARTMENTS_COLUMN_DATA_INDEX = {
    NAME: 'name',
    TYPE_CODE: 'shoreDepartmentTypeId',
    TYPE_CODE_FILTER: 'shoreDepartmentTypeCode',
    EMAIL: 'email',
    PHONE: 'phone',
    ENTITY_LOCATION_NAME: 'entityLocationId',
    ENTITY_LOCATION_NAME_FILTER: 'entityLocationName',
    MANAGER_NAME: 'managerId',
    MANAGER_NAME_FILTER: 'managerName',
    IS_INTERNAL: 'isInternal',
    IS_BUILT_IN: 'isBuiltIn',
};

export const SHORE_DEPARTMENTS_COLUMN_TITLE = {
    NAME: msg`Shore Dept Name*`,
    TYPE_CODE: msg`Shore Dept Type Code*`,
    EMAIL: msg`Shore Dept Mail`,
    PHONE: msg`Shore Dept Phone`,
    ENTITY_LOCATION_NAME: msg`Location / Entity`,
    MANAGER_NAME: msg`Manager`,
    IS_INTERNAL: msg`Internal`,
    IS_BUILT_IN: msg`Built-in`,
} as const;

export const SHORE_DEPARTMENTS_ENTITY_TYPE = 'ShoreDepartment';

export const SHORE_DEPARTMENTS_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    NAME: 'Name',
    TYPE_CODE: 'ShoreDepartmentTypeId',
    EMAIL: 'Email',
    PHONE: 'Phone',

    // only backend validation fields
    ENTITY_LOCATION_ID: 'EntityLocationId',
    MANAGER_ID: 'ManagerId',
} as const;

export const SHORE_DEPARTMENTS_COLUMN_SORT_MAP: Record<ShoreDepartmentsTypesColumns, string> = {
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.NAME]: SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.NAME,
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.TYPE_CODE]: 'shoreDepartmentTypeCode',
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.EMAIL]: SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.EMAIL,
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.PHONE]: SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.PHONE,
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.ENTITY_LOCATION_NAME]: 'entityLocationName',
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.MANAGER_NAME]: 'managerName',
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.IS_INTERNAL]: SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.IS_INTERNAL,
};

export const SHORE_DEPARTMENTS_DATASOURCE_REFS = {
    ENTITY_LOCATION: 'RefEntityLocations',
    SHORE_DEPARTMENT_TYPE: 'RefShoreDepartmentTypes',
    MANAGER: 'RefUsers',
};

export const SHORE_DEPARTMENTS_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.NAME]: SHORE_DEPARTMENTS_COLUMN_TITLE.NAME,
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.TYPE_CODE]: SHORE_DEPARTMENTS_COLUMN_TITLE.TYPE_CODE,
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.EMAIL]: SHORE_DEPARTMENTS_COLUMN_TITLE.EMAIL,
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.PHONE]: SHORE_DEPARTMENTS_COLUMN_TITLE.PHONE,
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.ENTITY_LOCATION_NAME]: SHORE_DEPARTMENTS_COLUMN_TITLE.ENTITY_LOCATION_NAME,
    [SHORE_DEPARTMENTS_COLUMN_DATA_INDEX.MANAGER_NAME]: SHORE_DEPARTMENTS_COLUMN_TITLE.MANAGER_NAME,
};

export const SHORE_DEPARTMENTS_DATA_PAYLOAD = {
    dataToReturn: [
        ADMIN_CENTER_DATA.SHORE_DEPARTMENTS,
        ADMIN_CENTER_DATA.SHORE_DEPARTMENT_TYPES,
        ADMIN_CENTER_DATA.SHORE_USERS,
        ADMIN_CENTER_DATA.ENTITY_LOCATION,
    ],
};
// shoreTeam-table
export const SHORE_TEAM_COLUMN_DATA_INDEX = {
    NAME: 'name',
    SHORE_DEPARTMENT_NAME: 'shoreDepartmentId',
    SHORE_DEPARTMENT_NAME_FILTER: 'shoreDepartmentName',
    EMAIL: 'email',
    PHONE: 'phone',
    ENTITY_LOCATION_NAME: 'entityLocationId',
    ENTITY_LOCATION_NAME_FILTER: 'entityLocationName',
    IS_BUILT_IN: 'isBuiltIn',
};

export const SHORE_TEAM_COLUMN_TITLE = {
    NAME: msg`Shore Team Name*`,
    SHORE_DEPARTMENT_NAME: msg`Shore Department*`,
    EMAIL: msg`Shore Team Mail`,
    PHONE: msg`Shore Team Phone`,
    ENTITY_LOCATION_NAME: msg`Location / Entity`,
    IS_BUILT_IN: msg`Built-in`,
} as const;

export const SHORE_TEAM_ENTITY_TYPE = 'ShoreTeam';

export const SHORE_TEAM_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    NAME: 'Name',
    SHORE_DEPARTMENT_ID: 'ShoreDepartmentId',
    EMAIL: 'Email',
    PHONE: 'Phone',

    // only backend validation fields
    ENTITY_LOCATION_NAME: 'EntityLocationId',
} as const;

export const SHORE_TEAM_COLUMN_SORT_MAP: Record<ShoreTeamTypesColumns, string> = {
    [SHORE_TEAM_COLUMN_DATA_INDEX.NAME]: 'name',
    [SHORE_TEAM_COLUMN_DATA_INDEX.EMAIL]: 'email',
    [SHORE_TEAM_COLUMN_DATA_INDEX.PHONE]: 'phone',
    [SHORE_TEAM_COLUMN_DATA_INDEX.SHORE_DEPARTMENT_NAME]: 'shoreDepartmentName',
    [SHORE_TEAM_COLUMN_DATA_INDEX.ENTITY_LOCATION_NAME]: 'entityLocationName',
};

export const SHORE_TEAM_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [SHORE_TEAM_COLUMN_DATA_INDEX.NAME]: SHORE_TEAM_COLUMN_TITLE.NAME,
    [SHORE_TEAM_COLUMN_DATA_INDEX.SHORE_DEPARTMENT_NAME]: SHORE_TEAM_COLUMN_TITLE.SHORE_DEPARTMENT_NAME,
    [SHORE_TEAM_COLUMN_DATA_INDEX.EMAIL]: SHORE_TEAM_COLUMN_TITLE.EMAIL,
    [SHORE_TEAM_COLUMN_DATA_INDEX.PHONE]: SHORE_TEAM_COLUMN_TITLE.PHONE,
    [SHORE_TEAM_COLUMN_DATA_INDEX.ENTITY_LOCATION_NAME]: SHORE_TEAM_COLUMN_TITLE.ENTITY_LOCATION_NAME,
};

export const SHORE_TEAM_DATASOURCE_REFS = {
    SHORE_DEPARTMENTS: 'RefShoreDepartments',
    ENTITY_LOCATIONS: 'RefEntityLocations',
};

export const SHORE_TEAM_DATA_PAYLOAD = {
    dataToReturn: [
        ADMIN_CENTER_DATA.SHORE_TEAMS,
        ADMIN_CENTER_DATA.SHORE_DEPARTMENTS,
        ADMIN_CENTER_DATA.ENTITY_LOCATION,
    ],
};
// fuelTypeCategories-table
export const FUEL_TYPE_CATEGORIES_COLUMN_DATA_INDEX = {
    NAME: 'name',
};

export const FUEL_TYPE_CATEGORIES_COLUMN_TITLE = {
    NAME: msg`Fuel Type Category`,
} as const;

export const FUEL_TYPE_CATEGORIES_COLUMN_SORT_MAP: Record<FuelTypeCategoriesTypesColumns, string> = {
    [FUEL_TYPE_CATEGORIES_COLUMN_DATA_INDEX.NAME]: FUEL_TYPE_CATEGORIES_COLUMN_DATA_INDEX.NAME,
};

// fuelTypes-table
export const FUEL_TYPES_COLUMN_DATA_INDEX = {
    NAME: 'name',
    CODE: 'code',
    TYPE_CATEGORY_NAME: 'fuelTypeCategoryId',
    TYPE_CATEGORY_NAME_FILTER: 'fuelTypeCategoryName',
};

export const FUEL_TYPES_COLUMN_TITLE = {
    NAME: msg`Fuel Type Name`,
    CODE: msg`Fuel Type Code`,
    TYPE_CATEGORY_NAME: msg`Fuel Type Category`,
} as const;

const FUEL_TYPES_COLUMN_COMMON = {
    [FUEL_TYPES_COLUMN_DATA_INDEX.NAME]: FUEL_TYPES_COLUMN_DATA_INDEX.NAME,
    [FUEL_TYPES_COLUMN_DATA_INDEX.CODE]: FUEL_TYPES_COLUMN_DATA_INDEX.CODE,
};

export const FUEL_TYPES_COLUMN_SORT_MAP: Record<FuelTypesTypesColumns, string> = {
    ...FUEL_TYPES_COLUMN_COMMON,
    [FUEL_TYPES_COLUMN_DATA_INDEX.TYPE_CATEGORY_NAME]: 'fuelTypeCategoryName',
};

export const FUEL_TYPES_DATASOURCE_REFS = {
    FUEL_TYPE_CATEGORIES: 'RefFuelTypeCategories',
};

// vesselGroups-table
export const VESSEL_GROUPS_COLUMN_DATA_INDEX = {
    NAME: 'name',
    SHORT_CODE: 'shortCode',
    IS_BUILT_IN: 'isBuiltIn',
};

export const VESSEL_GROUPS_COLUMN_TITLE = {
    NAME: msg`Group Name*`,
    SHORT_CODE: msg`Group short name*`,
    IS_BUILT_IN: msg`Built-in`,
} as const;

export const VESSEL_GROUPS_ENTITY_TYPE = 'VesselGroup';

export const VESSEL_GROUPS_VALIDATION_FIELDS_NAMES = {
    // frontend+backend validation fields
    NAME: 'Name',
    SHORT_CODE: 'ShortCode',
} as const;

export const VESSEL_GROUPS_COLUMN_SORT_MAP: Record<VesselGroupsTypesColumns, string> = {
    [VESSEL_GROUPS_COLUMN_DATA_INDEX.NAME]: VESSEL_GROUPS_COLUMN_DATA_INDEX.NAME,
    [VESSEL_GROUPS_COLUMN_DATA_INDEX.SHORT_CODE]: VESSEL_GROUPS_COLUMN_DATA_INDEX.SHORT_CODE,
};

export const VESSEL_GROUPS_COLUMN_TITLE_MAP: Record<string, MessageDescriptor> = {
    [VESSEL_GROUPS_COLUMN_DATA_INDEX.NAME]: VESSEL_GROUPS_COLUMN_TITLE.NAME,
    [VESSEL_GROUPS_COLUMN_DATA_INDEX.SHORT_CODE]: VESSEL_GROUPS_COLUMN_TITLE.SHORT_CODE,
};
